import { FC, useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, Table, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

import { resendEmails } from '../../gql/dealGql';
import {
  DealDocument,
  DocumentProgressStatus,
  DocumentSignatureStatus,
  Maybe,
  useRetrieveDealDocsUploadedToR1LazyQuery,
} from '../../gql/generated/graphql';

import ContractNotFoundModal from '../DealInfoBuyout/components/modals/ContractNotFoundModal';
import Modal from '../shared/Modal';

import { PermissionEnum } from '../../constants/permissions';
import { DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import { AbilityContext } from '../../libs/contextLib';
import { NonButtonStep } from '../../pages/DealDetail/ContractStatusTimeline/NonButtonStep';
import {
  CONTRACT_NOT_FOUND_ERROR,
  isDocumentProgressSent,
  isDocumentProgressUploaded,
  isDocumentProgressValidated,
} from '../../utils/routeOne';
import { snakeCaseToTitleCase } from '../../utils/text';

interface SigningModalButtonProps {
  width?: string;
}

const formatSignatureDate = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  return format(date, "M/d 'at' h:mm a");
};

const SigningModalButton: FC<SigningModalButtonProps> = ({ width }) => {
  const {
    deal: { id, document_progress_status, signing_on_com },
  } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  const [documents, setDocuments] = useState<Maybe<DealDocument>[]>([]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isOpenContractNotFound,
    onClose: onCloseContractNotFound,
    onOpen: onOpenContractNotFound,
  } = useDisclosure();

  const allDocumentsAreSigned = documents.every(
    (document) => document?.signatureStatus === DocumentSignatureStatus.Completed,
  );
  const showLabel = !signing_on_com;
  const isDisabled =
    (!isDocumentProgressValidated(document_progress_status) ||
      !isDocumentProgressUploaded(document_progress_status) ||
      !isDocumentProgressSent(document_progress_status) ||
      document_progress_status !== DocumentProgressStatus.SentForSignatures) &&
    !abilities.has(PermissionEnum.SuperUser) &&
    !abilities.has(PermissionEnum.FundingManager);

  const [resendEmailsMutation, { loading: isResendingEmails }] = useMutation<{
    resendEmails?: boolean;
  }>(resendEmails, {
    variables: {
      dealId: id,
    },
    onCompleted: (data) => {
      toast.success(
        data?.resendEmails ? 'Emails resent successfully' : 'No emails needed resending',
      );
      onClose();
    },
    onError: (e) => {
      onClose();

      const error = e as Error;
      if (error.message === CONTRACT_NOT_FOUND_ERROR) {
        onOpenContractNotFound();
        return;
      }

      logger.error('SigningModalButton.tsx', 'resendEmailsMutation', { id }, e);
      toast.error(`Error resending emails: ${e.message ?? e}`);
    },
  });

  const [retrieveDealDocsUploadedToR1, { loading: isRetrievingDocsUploadedToR1 }] =
    useRetrieveDealDocsUploadedToR1LazyQuery({
      variables: { dealId: id ?? -1 },
      fetchPolicy: 'no-cache',
      onCompleted(newData) {
        setDocuments(newData?.retrieveDealDocsUploadedToR1 ?? []);
      },
      onError: (e) => {
        logger.error('SigningButton.tsx', 'retrieveDealDocsUploadedToR1', { id }, e);
        toast.error(`Error retrieving documents uploaded to R1: ${e.message ?? e}`);
      },
    });

  return (
    <>
      {showLabel ? (
        <NonButtonStep width={width}>SIGNING</NonButtonStep>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          w={width}
          onClick={async () => {
            await retrieveDealDocsUploadedToR1();
            onOpen();
          }}
          isDisabled={isDisabled}
          isLoading={isRetrievingDocsUploadedToR1}
          loadingText="SIGNING"
        >
          SIGNING
        </Button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title="Signature Status"
        canDismiss={!isResendingEmails}
        rightButtons={
          <>
            <Button
              variant="secondary"
              onClick={async () => {
                await resendEmailsMutation();
              }}
              isDisabled={allDocumentsAreSigned}
              isLoading={isResendingEmails}
              loadingText="RESEND EMAILS"
            >
              RESEND EMAILS
            </Button>
            <Button onClick={onClose} isDisabled={isResendingEmails}>
              OK
            </Button>
          </>
        }
      >
        {documents.length === 0 ? (
          <Text>No documents uploaded to R1</Text>
        ) : (
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>Document</Th>
                <Th>Signature Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {documents.map((document) => (
                <Tr key={document?.documentName}>
                  <Td>{document?.displayName}</Td>
                  <Td>
                    {snakeCaseToTitleCase(document?.signatureStatus ?? '')}
                    {document?.completedAt && ` ${formatSignatureDate(document.completedAt)}`}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Modal>

      <ContractNotFoundModal
        isOpen={isOpenContractNotFound}
        onClose={onCloseContractNotFound}
        handleOk={() => {
          onCloseContractNotFound();
          onClose();
        }}
      />
    </>
  );
};

export default SigningModalButton;
